import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
  chatbotActions,
  DEFAULT_CHATBOT_ERROR_MESSAGE,
  REQUEST_STATUS,
} from '../store';

// TODO: as llm and other features are added we may want to
// adjust error display delay to longer time
const POST_ACTIVITY_ERROR_DELAY_MS = 7000;

/**
 * Defers surfacing a failed postActivity as a user-visible error. Direct
 * Line can briefly report transport timing states before bot
 * `typing`/`message` activities arrive, so the failure waits
 * POST_ACTIVITY_ERROR_DELAY_MS and a quick bot response (which calls
 * clearPendingPostError) cancels it before it flashes.
 *
 * Also owns the feedback rollback: when a failed post was a feedback
 * submission, its prompt id is held until a terminal failure actually
 * surfaces, at which point feedbackPending is cleared so the icons
 * re-enable for a retry.
 *
 * The caller wires in what else happens when the error surfaces
 * (resetTypingAnnounced, focusComposerInput) so cross-concern
 * choreography stays visible at the call site.
 */
export default function useDeferredPostError({
  focusComposerInput,
  resetTypingAnnounced,
}) {
  const dispatch = useDispatch();
  const pendingPostErrorTimerRef = useRef(null);
  const pendingFeedbackMessageIdRef = useRef(null);

  const clearPendingPostError = useCallback(() => {
    if (pendingPostErrorTimerRef.current) {
      clearTimeout(pendingPostErrorTimerRef.current);
      pendingPostErrorTimerRef.current = null;
    }
    // Intentionally do NOT clear pendingFeedbackMessageIdRef here. Cancelling
    // the deferred error timer (e.g. because the user is starting a new send,
    // or because the bot just sent typing/a message) does not mean we've
    // resolved the in-flight feedback rollback. The ref is consumed and
    // nulled inside the timer body when it actually fires; that is the only
    // place we know we've finished with it.
  }, []);

  const scheduleDeferredPostError = useCallback(
    pendingFeedbackMessageId => {
      clearPendingPostError();
      // Only overwrite the ref when this call carries a feedback id. A later
      // non-feedback failure must not drop a rollback we still owe the
      // feedback icons from an earlier failed feedback POST.
      if (pendingFeedbackMessageId) {
        pendingFeedbackMessageIdRef.current = pendingFeedbackMessageId;
      }
      pendingPostErrorTimerRef.current = setTimeout(() => {
        resetTypingAnnounced();
        dispatch(chatbotActions.setRequestStatus(REQUEST_STATUS.FAILED));
        dispatch(chatbotActions.setChatError(DEFAULT_CHATBOT_ERROR_MESSAGE));
        if (pendingFeedbackMessageIdRef.current) {
          // Only clear feedback pending when a terminal transport failure
          // actually surfaces to the user, so the icons re-enable for a retry.
          dispatch(
            chatbotActions.clearFeedbackPending(
              pendingFeedbackMessageIdRef.current,
            ),
          );
          pendingFeedbackMessageIdRef.current = null;
        }
        focusComposerInput();
        pendingPostErrorTimerRef.current = null;
      }, POST_ACTIVITY_ERROR_DELAY_MS);
    },
    [clearPendingPostError, dispatch, focusComposerInput, resetTypingAnnounced],
  );

  useEffect(() => {
    return () => {
      clearPendingPostError();
    };
  }, [clearPendingPostError]);

  return {
    clearPendingPostError,
    scheduleDeferredPostError,
  };
}
